import { useCallback } from 'react';
import amplitudeLog from '@client/amplitude';
import { AMP_PROPOSAL_DRAG_AND_DROP } from '@shared/constants';
import { DropType, IDroppableData } from '@ui/drag-and-drop/Droppable';
import {
  isElementWithGeneratedChildren,
  isGenerated,
} from '@shared/helpers/element.helpers';
import {
  addElements,
  removeElements,
  updateElements,
} from '@shared/helpers/project.helpers';
import {
  getChildElements,
  getElementById,
  getParentElement,
  isBuildingVersionElement,
  isOneOfChildElements,
  isParentElement,
  isProductElement,
} from '@shared/helpers/recursive-element.helpers';
import {
  Baseline,
  OneOfChildElements,
  OneOfParentElements,
  OneOfPropertyElements,
  Project,
} from '@shared/models/project.model';
import { getProject, useUpdateProject } from '@client/store/project';
import { getSelectedVersion } from '@client/store/ui';
import { IInsertSortPlacement } from '@shared/models/sort.model';
import { isProductCategoryElement } from '@shared/helpers/element-category.helpers';
import { ItemOrItemId, ValidIdItem } from '@shared/models/type-helpers.model';
import { sortInsert } from '@shared/helpers/sort.helpers';
import { useProposals } from '@features/proposals/use-proposals.hooks';
import { getProposalById } from '@shared/helpers/proposal.helpers';
import { useRecipeAndCustomElementProperties } from '@client/hooks/use-element-properties.hooks';

interface IUseMoveElements {
  moveElementsTo: (
    moveIntoId: string,
    moveType: DropType,
    ...elementIds: string[]
  ) => Promise<Project>;
  isMoveElementsAllowed: (
    moveToId: string | undefined,
    moveType?: DropType,
    ...elementIds: string[]
  ) => boolean;
}

export const useMoveElements = (): IUseMoveElements => {
  const updateProject = useUpdateProject();
  const isMoveElementsAllowed = useCallback(
    (
      moveToId: string | undefined,
      moveType?: DropType,
      ...elementIds: string[]
    ) => {
      const selectedVersion = getSelectedVersion();

      if (!selectedVersion || elementIds.length === 0) {
        return false;
      }

      const moveIntoElement = getMoveIntoElement(
        selectedVersion,
        moveToId,
        moveType,
      );

      // Can't move element before or after a version
      if (
        moveType !== 'inside' &&
        isBuildingVersionElement(moveIntoElement) &&
        moveIntoElement.id === moveToId
      ) {
        return false;
      }

      // Can't move element to a product element
      if (!moveIntoElement || !isParentElement(moveIntoElement)) {
        return false;
      }

      // Can't move element to an element with generated children
      if (
        isElementWithGeneratedChildren(moveIntoElement) ||
        isProductCategoryElement(moveIntoElement)
      ) {
        return false;
      }

      const elementsCanBeMoved = elementIds.every((elementId) => {
        const element = getElementById(selectedVersion, elementId);
        const children = getChildElements(moveIntoElement);

        // Can't move element into itself
        if (!element || element.id === moveIntoElement.id) {
          return false;
        }

        // Can't move generated elements
        if (isGenerated(element)) {
          return false;
        }

        // Can't move product elements into a building version
        if (
          isProductElement(element) &&
          isBuildingVersionElement(moveIntoElement)
        ) {
          return false;
        }

        // Can't move element below or above itself
        if (moveType !== 'inside' && moveToId === elementId) {
          return false;
        }

        // Can't move element to the parent since it's already there
        if (
          moveType === 'inside' &&
          children.some((child) => child.id === element.id)
        ) {
          return false;
        }

        // Can't move element to a child element
        return !getElementById(element, moveIntoElement.id);
      });
      return elementsCanBeMoved;
    },
    [],
  );

  const moveElementsTo = useCallback(
    async (moveToId: string, moveType?: DropType, ...elementIds: string[]) => {
      if (!isMoveElementsAllowed(moveToId, moveType, ...elementIds)) {
        throw new Error('Move not allowed');
      }

      const project = getProject();

      const projectWithMovedElements = moveElements(
        project,
        moveToId,
        moveType,
        ...elementIds,
      );

      // If all elements are already in the right "folder" we don't need to update the project
      if (projectWithMovedElements !== project) {
        // TODO(replay): express as ProjectChange so a 422 race can be replayed.
        return await updateProject({
          kind: 'snapshot',
          project: projectWithMovedElements,
        });
      }

      return projectWithMovedElements;
    },
    [isMoveElementsAllowed, updateProject],
  );

  return { moveElementsTo, isMoveElementsAllowed };
};

export const useMoveProposal = () => {
  const proposals = useProposals();
  const updateProject = useUpdateProject();

  return useCallback<IDroppableData['onDrop']>(
    (dragId, dropId, dropType) => {
      const version = getSelectedVersion(true);
      const updatedProposals = moveItem(proposals, dragId, dropId, dropType);

      const updatedProject = updateElements(getProject(), {
        id: version.id,
        proposals: updatedProposals,
      });
      // TODO(replay): express as ProjectChange so a 422 race can be replayed.
      updateProject({ kind: 'snapshot', project: updatedProject });
      amplitudeLog(AMP_PROPOSAL_DRAG_AND_DROP, { ProposalId: dragId });
    },
    [proposals, updateProject],
  );
};

export const useMoveProperty = (element: OneOfPropertyElements) => {
  const updateProject = useUpdateProject();
  const properties = useRecipeAndCustomElementProperties(element);

  return useCallback<IDroppableData['onDrop']>(
    (dragId, dropId, dropType) => {
      const updatedProperties = moveItem(properties, dragId, dropId, dropType);
      const updatedProject = updateElements(getProject(), {
        id: element.id,
        properties: updatedProperties,
      });
      // TODO(replay): express as ProjectChange so a 422 race can be replayed.
      updateProject({ kind: 'snapshot', project: updatedProject });
    },
    [element.id, properties, updateProject],
  );
};

export const useMoveProposalIsAllowed = () => {
  return useCallback((dragId: string) => {
    const version = getSelectedVersion(true);
    const proposal = getProposalById(version, dragId, false);
    return proposal !== undefined;
  }, []);
};

/**
 * Get element in which element will be moved to.
 * If moveType is 'inside' it will return the element itself.
 * But if it's 'above' or 'below' it will return the parent.
 * @param moveToId
 * @param moveType
 * @returns
 */
const getMoveIntoElement = <R extends Baseline | Project>(
  root: R,
  moveToId?: string,
  moveType: DropType = 'inside',
): OneOfParentElements | undefined => {
  if (typeof moveToId !== 'string') {
    throw new Error('selected version not found');
  }

  const moveToElement = getElementById(root, moveToId);

  // After or behind will move the element into the parent
  const moveIntoElement =
    moveType === 'inside'
      ? moveToElement
      : getParentElement(root, moveToElement);

  return isParentElement(moveIntoElement) ? moveIntoElement : undefined;
};

export const moveElements = <R extends Baseline | Project>(
  root: R,
  moveToId: string,
  moveType: DropType = 'inside',
  ...elementOrIds: ItemOrItemId<OneOfChildElements>[]
): R => {
  if (elementOrIds.length === 0) {
    return root;
  }

  // Get the parent element to move the elements into
  const parent = getMoveIntoElement(root, moveToId, moveType);

  if (!parent || !isParentElement(parent)) {
    throw new Error('Could not find parent element to move to');
  }

  // Get elements to move
  const elements = elementOrIds
    .map((e) => (typeof e === 'string' ? getElementById(root, e) : e))
    .filter(isOneOfChildElements)
    .filter(isOneOfChildElements);

  const placement = moveTypeToSortPlacement(moveType);
  const siblingId = parent.id === moveToId ? undefined : moveToId;

  // Remove elements from old positions and keep the updated project & version
  root = removeElements(root, ...elements);

  return addElements(
    root,
    parent,
    { placement, siblingId, regenerateIds: false, generateUniqueName: false },
    ...elements,
  ).root;
};

const moveItem = <T extends ValidIdItem>(
  items: T[],
  dragId?: T['id'],
  dropId?: T['id'],
  dropType?: DropType,
): T[] => {
  const dragItem = items.find((p) => p.id === dragId);
  const target = items.find((p) => p.id === dropId);

  if (!dragItem || !target || !dropType) {
    return items;
  }
  return sortInsert(moveTypeToSortPlacement(dropType), items, target, dragItem);
};

const moveTypeToSortPlacement = (dropType: DropType): IInsertSortPlacement => {
  if (dropType === 'inside') return 'last';
  if (dropType === 'above') return 'before';
  if (dropType === 'below') return 'after';
  throw new Error('Invalid drop type');
};
